'use client'
import { Switch } from '@repo/ui'
import { Settings } from '@repo/ui/icons'
import { memo, type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react'

import { clientEnvs } from '@/config/client-envs'
import { type DebugOverrides, useDebugOverrides } from '@/contexts/DebugOverrides'

const DRAG_THRESHOLD = 4

type Position = {
  x: number
  y: number
}

type DragState = {
  initialPosition: Position
  startX: number
  startY: number
  moved: boolean
}

const TOGGLES: { key: keyof DebugOverrides; label: string }[] = [
  { key: 'hasLegacyMorpho', label: 'Legacy MORPHO Holder' },
  { key: 'isUkUser', label: 'UK User' },
  { key: 'showEmbedderBanner', label: 'Embedded (partner) banner' },
  { key: 'hasDeprecatedVaultPosition', label: 'Deprecated vault position' }
]

/**
 * Draggable debug panel that overrides the wallet / user-state signals
 * that drive the notification surfaces (status toasts, the wallet
 * button/dropdown badge, and the "Your deposits" copy tweak).
 *
 * Gated on `NEXT_PUBLIC_SHOW_NOTIFICATIONS_DEBUG_PANEL` — enabled on local
 * + Vercel preview, off in staging / production. Overrides live in memory
 * (DebugOverrideContext) — not persisted.
 */
const NotificationsDebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const { overrides, setOverride, clearAll, isActive } = useDebugOverrides()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dragStateRef = useRef<DragState | null>(null)
  const suppressClickRef = useRef(false)

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    const handleMouseDownOutside = (event: MouseEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return
      setIsOpen(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDownOutside)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleMouseDownOutside)
    }
  }, [isOpen])

  // Attach drag listeners only while a drag is in progress. Routing this through
  // a `useEffect` keyed on `isDragging` guarantees the listeners detach on
  // component unmount mid-drag, not just on mouseup / blur.
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (event: MouseEvent) => {
      const dragState = dragStateRef.current
      if (!dragState) return

      const nextX = dragState.initialPosition.x + event.clientX - dragState.startX
      const nextY = dragState.initialPosition.y + event.clientY - dragState.startY

      if (
        !dragState.moved &&
        (Math.abs(nextX - dragState.initialPosition.x) >= DRAG_THRESHOLD ||
          Math.abs(nextY - dragState.initialPosition.y) >= DRAG_THRESHOLD)
      ) {
        dragState.moved = true
      }

      setPosition({ x: nextX, y: nextY })
    }

    const stopDrag = () => {
      const dragState = dragStateRef.current
      if (dragState) suppressClickRef.current = dragState.moved
      dragStateRef.current = null
      setIsDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', stopDrag)
    // Clear drag if the window loses focus mid-drag (e.g., Cmd-Tab).
    window.addEventListener('blur', stopDrag)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', stopDrag)
      window.removeEventListener('blur', stopDrag)
    }
  }, [isDragging])

  const handleTriggerMouseDown = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return

    dragStateRef.current = {
      initialPosition: position,
      startX: event.clientX,
      startY: event.clientY,
      moved: false
    }

    suppressClickRef.current = false
    setIsDragging(true)
  }

  const handleTriggerClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }

    setIsOpen(current => !current)
  }

  if (!clientEnvs.NEXT_PUBLIC_SHOW_NOTIFICATIONS_DEBUG_PANEL) return null

  return (
    <div
      ref={rootRef}
      data-testid="notifications-debug-panel-root"
      className="fixed bottom-[120px] left-4 z-60"
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
    >
      {isOpen && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Notification overrides"
          className="border-border bg-popover mb-xs w-72 rounded-2xl border p-[16px] shadow-lg"
        >
          <div className="flex items-center justify-between">
            <span className="text-foreground text-sm" style={{ lineHeight: 'normal' }}>
              Notification overrides
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="hover:bg-muted text-foreground hover:text-foreground focus-visible:ring-border px-xs rounded-lg py-0.5 text-xs focus-visible:ring-2 focus-visible:outline-none"
            >
              Clear All
            </button>
          </div>

          <div className="mt-sm flex flex-col gap-2.5">
            {TOGGLES.map(({ key, label }) => (
              <div className="flex items-center justify-between" key={key}>
                <span className="text-foreground text-xs" style={{ lineHeight: 'normal' }}>
                  {label}
                </span>
                <Switch
                  checked={overrides[key] === true}
                  aria-label={`Toggle ${label}`}
                  onCheckedChange={newState => setOverride(key, newState)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        onMouseDown={handleTriggerMouseDown}
        aria-label={
          isActive
            ? 'Toggle notifications debug panel (overrides active)'
            : 'Toggle notifications debug panel'
        }
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className="border-border bg-popover relative flex h-10 w-10 cursor-grab touch-none items-center justify-center rounded-full border shadow-md transition-transform hover:scale-105 active:cursor-grabbing"
        title="Notifications debug panel"
      >
        <Settings className="text-foreground" aria-hidden="true" />
        {isActive && (
          <span
            data-testid="notifications-debug-panel-active-indicator"
            className="bg-primary/75 h-xs absolute top-0 right-0 w-2 rounded-full"
          />
        )}
      </button>
    </div>
  )
}

export default memo(NotificationsDebugPanel)
